import React from "react";
import { useNavigate } from "react-router-dom";

function StatusBadge({ status, statusLabel, scheduledToday }) {
  if (status === "not_due" && !statusLabel) {
    return null;
  }

  const statusMap = {
    em_dia: {
      label: "EM DIA",
      className: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    },
    atrasada: {
      label: "ATRASADA",
      className: "bg-red-50 text-red-700 border-red-200/80 animate-pulse",
    },
    atrasado: {
      label: "ATRASADO",
      className: "bg-red-50 text-red-700 border-red-200/80 animate-pulse",
    },
    programada: {
      label: "PROGRAMADA",
      className: "bg-amber-50 text-amber-800 border-amber-200/80",
    },
    programado: {
      label: "PROGRAMADO",
      className: "bg-amber-50 text-amber-800 border-amber-200/80",
    },
    complete: {
      label: "✓ Registrado hoje",
      className: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    },
    partial: {
      label: "EM ANDAMENTO",
      className: "bg-purple-50 text-purple-700 border-purple-200/80",
    },
    pending: {
      label: "Hoje",
      className: "bg-amber-50 text-amber-800 border-amber-200/80",
    },
    overdue: {
      label: "Atrasado",
      className: "bg-red-50 text-red-700 border-red-200/80 animate-pulse",
    },
  };

  const current = statusMap[status] || {
    label: statusLabel || "Hoje",
    className: "bg-purple-50 text-purple-700 border-purple-200/80",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider shrink-0 whitespace-nowrap ${current.className}`}
    >
      {statusLabel || current.label}
    </span>
  );
}

function CareIcon({ iconName }) {
  switch (iconName) {
    case "droplet":
      return <span className="text-base">💧</span>;
    case "pill":
      return <span className="text-base">💊</span>;
    case "sparkles":
    case "supplements":
      return <span className="text-base">✨</span>;
    case "scale":
      return <span className="text-base">⚖️</span>;
    case "ruler":
      return <span className="text-base">📏</span>;
    case "heart":
      return <span className="text-base">❤️</span>;
    case "blood":
      return <span className="text-base">🩸</span>;
    default:
      return <span className="text-base">✨</span>;
  }
}

function renderFormattedText(text) {
  if (!text) return null;
  const match = String(text).match(/^(.*?\bhá\s+)(\d+)(\s+dias?.*)$/i);
  if (match) {
    return (
      <span>
        {match[1]}
        <strong className="font-bold text-[#24152F]">{match[2]}</strong>
        {match[3]}
      </span>
    );
  }
  return text;
}

export default function HomeCareCard({ card, onQuickAction }) {
  const navigate = useNavigate();

  if (!card) return null;

  const {
    title,
    icon,
    status,
    statusLabel,
    scheduledToday,
    lastValue,
    lastValueSub,
    lastDate,
    medicationDetail,
    medicationQuantity,
    medicationTime,
    measurementLabel,
    measurementValue,
    measurementNext,
    quickAction,
    route,
  } = card;

  const handleCardClick = () => {
    if (route) {
      navigate(route);
    }
  };

  const handleActionClick = (e) => {
    e.stopPropagation();
    if (onQuickAction) {
      onQuickAction(quickAction, card);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="glass-card liti-card rounded-3xl p-4 transition-all duration-300 hover:shadow-md cursor-pointer border border-[#ECE7FA] mb-3 font-sans group"
    >
      {/* LINHA DE CABEÇALHO (100% DA LARGURA DO CARD): TÍTULO À ESQUERDA | BADGE DE STATUS FLUSH À DIREITA */}
      <div className="flex items-center justify-between gap-2 w-full mb-1.5 overflow-hidden">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm leading-none shrink-0">
            <CareIcon iconName={icon} />
          </span>
          <span className="liti-card-title text-xs font-bold uppercase tracking-wider text-[#6D6680] truncate">
            {title}
          </span>
        </div>
        <div className="shrink-0">
          <StatusBadge status={status} statusLabel={statusLabel} scheduledToday={scheduledToday} />
        </div>
      </div>

      {/* LINHA DE CONTEÚDO: DETALHES DO CARD À ESQUERDA | CHEVRON '>' À DIREITA */}
      <div className="flex items-center justify-between gap-2 w-full overflow-hidden">
        <div className="flex-1 min-w-0 space-y-0.5">
          {medicationDetail ? (
            <div>
              <p className="liti-card-body text-xs font-bold text-[#24152F] leading-snug">
                {renderFormattedText(lastValue)}
              </p>
              <p className="text-xs sm:text-sm font-semibold text-[#24152F] leading-snug break-words mt-0.5">
                {medicationDetail}
              </p>
              {medicationQuantity && (
                <p className="text-xs font-semibold text-[#24152F] leading-snug">
                  {medicationQuantity}
                </p>
              )}
              {medicationTime && (
                <p className="text-xs font-medium text-[#6D6680] leading-snug pt-0.5">
                  {medicationTime}
                </p>
              )}
            </div>
          ) : measurementValue ? (
            <div>
              <p className="liti-card-body text-xs font-bold text-[#24152F] leading-snug">
                {renderFormattedText(lastValue)}
              </p>
              {measurementLabel && (
                <p className="text-xs font-semibold text-gray-600 leading-snug mt-0.5">
                  {measurementLabel}{" "}
                  <span className="font-bold text-[#24152F]">{measurementValue}</span>
                </p>
              )}
              {measurementNext && (
                <p className="text-xs font-medium text-[#6D6680] leading-snug">
                  Próximo registro:{" "}
                  <span className="font-bold text-[#24152F]">{measurementNext}</span>
                </p>
              )}
            </div>
          ) : (
            <div>
              <p className="liti-card-body text-xs font-bold text-[#24152F] leading-snug">
                {renderFormattedText(lastValue) || "—"}
              </p>

              {lastValueSub && (
                <p className="text-xs font-semibold text-gray-600 leading-snug mt-0.5">
                  {lastValueSub}
                </p>
              )}

              {!lastValueSub && lastDate && (
                <p className="text-xs font-medium text-[#6D6680] truncate mt-0.5">
                  Último registro • {lastDate}
                </p>
              )}
            </div>
          )}
        </div>

        {/* LADO DIREITO DO CORPO: CHEVRON '>' (OU BOTÃO DE INCREMENTO DE ÁGUA) */}
        <div className="flex items-center justify-center shrink-0 self-center z-10 pl-1">
          {quickAction?.type === "increment_water" ? (
            <button
              type="button"
              onClick={handleActionClick}
              className="btn-action-liti h-9 px-3.5 rounded-full bg-[#6C54D8] hover:bg-[#5841C5] active:scale-95 text-white text-xs font-bold transition-all duration-200 cursor-pointer shadow-[0_4px_14px_rgba(108,84,216,0.25)] whitespace-nowrap"
            >
              {quickAction.label || "+ 1 copo"}
            </button>
          ) : (
            <div className="p-1 text-[#6C54D8] group-hover:translate-x-0.5 transition-transform duration-200">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
